import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ZpHeadLayout from './ZpHeadLayout';
import { db } from '../../firebase';
import { ref, onValue } from 'firebase/database';
import { 
    FaArrowLeft, 
    FaSort,
    FaEye,
    FaFileAlt,
    FaSearch
} from 'react-icons/fa';

const ZpHeadInterestedWorkByDate = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // Determine if we are looking at Engineers or Societies based on the path
    const isSociety = location.pathname.includes('society-interested');
    const department = isSociety ? 'society' : 'subhe';

    const [works, setWorks] = useState([]);
    const [registrations, setRegistrations] = useState([]);
    const [meetings, setMeetings] = useState([]);
    const [selectedMeetingId, setSelectedMeetingId] = useState('');
    const [appliedMeetingId, setAppliedMeetingId] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);

        // Fetch Meetings/Dates from generatework
        const generateWorkRef = ref(db, 'ekamwatapzp/generatework');
        const unsubMeetings = onValue(generateWorkRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const dateList = Object.keys(data).map(dateKey => ({
                    id: dateKey,
                    date: dateKey
                }));
                // Sort by date (assuming DD-MM-YYYY format, might need manual sort if not string-sortable)
                setMeetings(dateList.reverse());
            } else {
                setMeetings([]);
            }
        });

        // Fetch Works based on appliedMeetingId
        let worksRef;
        if (appliedMeetingId) {
            worksRef = ref(db, `ekamwatapzp/generatework/${appliedMeetingId}`);
        } else {
            worksRef = ref(db, 'ekamwatapzp/WorkNames');
        }

        const unsubWorks = onValue(worksRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                let flattenedWorks = [];
                
                // If fetching from generatework, the structure is already flattened or needs different handling
                // Based on previous tool calls, generatework stores the worksToMove object
                
                Object.entries(data).forEach(([key, val]) => {
                    if (val && typeof val === 'object') {
                        if (val.schemeName) {
                            flattenedWorks.push({ id: key, meetingNumber: val.meetingNumber || "General", ...val });
                        } else {
                            Object.entries(val).forEach(([id, value]) => {
                                flattenedWorks.push({ id, meetingNumber: key, ...value });
                            });
                        }
                    }
                });
                setWorks(flattenedWorks);
            } else {
                setWorks([]);
            }
        });

        // Fetch Registrations
        const engRef = ref(db, 'ekamwatapzp/EngineerRegistrations');
        const socRef = ref(db, 'ekamwatapzp/SocietyRegistrations');

        let engData = {}, socData = {};
        let engDone = false, socDone = false;

        const checkRegDone = () => {
            if (engDone && socDone) {
                const allRegs = [
                    ...Object.entries(engData).map(([key, val]) => ({ ...val, _key: key })),
                    ...Object.entries(socData).map(([key, val]) => ({ ...val, _key: key }))
                ];
                setRegistrations(allRegs);
                setLoading(false);
            }
        };

        const unsubEng = onValue(engRef, (snapshot) => {
            engData = snapshot.val() || {};
            engDone = true;
            checkRegDone();
        });

        const unsubSoc = onValue(socRef, (snapshot) => {
            socData = snapshot.val() || {};
            socDone = true;
            checkRegDone();
        });

        return () => {
            unsubMeetings();
            unsubWorks();
            unsubEng();
            unsubSoc();
        };
    }, [appliedMeetingId]);

    const handleSearch = () => {
        setAppliedMeetingId(selectedMeetingId);
    };

    // Filter and Process Data
    const filteredWorks = useMemo(() => {
        const deptMap = { 'subhe': 'सुबे', 'society': 'मजूर' };
        return works.filter(work => {
            // Since we pull works from the specific date node already in the ref, 
            // the dateMatch is essentially always true if we matched the node.
            // But we keep it for consistency or if we fallback to WorkNames.
            const dateMatch = !appliedMeetingId || work.meetingNumber === appliedMeetingId || true; 
            const deptMatch = !department || work.sharedwork === deptMap[department];
            return deptMatch;
        });
    }, [works, department, appliedMeetingId]);

    // Interest Counts
    const workInterestMap = useMemo(() => {
        const counts = {};
        registrations.forEach(reg => {
            if (reg.work_payment) {
                Object.entries(reg.work_payment).forEach(([meetingNum, paymentData]) => {
                    const selected = paymentData.selectedWorks || [];
                    selected.forEach(workStr => {
                        const workNum = workStr.split(' - ')[0];
                        const key = `${meetingNum}_${workNum}`;
                        counts[key] = (counts[key] || 0) + 1;
                    });
                });
            }
        });
        return counts;
    }, [registrations]);

    const displayData = useMemo(() => {
        return filteredWorks.map(work => ({
            ...work,
            interestedCount: workInterestMap[`${work.meetingNumber}_${work.workNumber}`] || 0
        }));
    }, [filteredWorks, workInterestMap]);

    if (loading) {
        return (
            <div className="p-20 flex flex-col items-center justify-center space-y-4">
                <div className="animate-spin text-[#22165f] text-4xl">
                    <FaSort />
                </div>
                <p className="text-gray-400 font-bold animate-pulse uppercase tracking-widest text-xs tracking-widest">माहिती लोड होत आहे...</p>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8 bg-gray-50/50 min-h-screen font-marathi">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4 px-2">
                <div className="flex items-center gap-4">
                    <button 
                        onClick={() => navigate(-1)}
                        className="p-2.5 bg-white rounded-xl shadow-sm border border-gray-100 text-gray-400 hover:text-[#22165f] transition active:scale-95"
                    >
                        <FaArrowLeft />
                    </button>
                    <h1 className="text-2xl font-bold text-gray-800 tracking-tight">लॉटरीची लिस्ट</h1>
                </div>
                <div className="flex items-center gap-2 text-[12px] font-bold text-gray-400 uppercase tracking-wider">
                    सांगली जिल्हा परिषद / <span className="text-gray-600">लॉटरी लिस्ट</span>
                </div>
            </div>

            {/* Date Filter Card - Matching IMAGE layout */}
            <div className="bg-white rounded-xl shadow-md border border-gray-100 p-8 mb-8 border-t-4 border-t-blue-500">
                <div className="flex flex-col gap-3">
                    <label className="text-sm font-bold text-gray-700 flex items-center gap-2">
                        लॉटरीची तारीख निवडा <span className="text-red-500">*</span>
                    </label>
                    <div className="flex flex-col md:flex-row gap-4 max-w-2xl">
                        <div className="relative flex-grow">
                            <select 
                                className="w-full bg-gray-50 border-2 border-gray-200 rounded-lg py-3 px-4 focus:ring-2 focus:ring-blue-500/20 outline-none transition font-medium appearance-none"
                                value={selectedMeetingId}
                                onChange={(e) => setSelectedMeetingId(e.target.value)}
                            >
                                <option value="">लॉटरीची तारीख निवडा</option>
                                {meetings.map(m => (
                                    <option key={m.id} value={m.id}>{m.date}</option>
                                ))}
                            </select>
                            <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 font-black">
                                ⌵
                            </div>
                        </div>
                        <button 
                            onClick={handleSearch}
                            className="bg-[#4e73df] hover:bg-blue-700 text-white px-10 py-3 rounded-lg font-bold shadow-md transition transform active:scale-95"
                        >
                            Search
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content Table Card */}
            <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-[#f8f9fa] border-b border-gray-200">
                            <tr className="text-[13px] font-black text-gray-600 uppercase tracking-tight">
                                <th className="px-6 py-5 border-r border-gray-100 w-16 text-center">#</th>
                                <th className="px-6 py-5 border-r border-gray-100 w-24">वर्क नंबर</th>
                                <th className="px-6 py-5 border-r border-gray-100 min-w-[300px]">कामाचे नाव</th>
                                <th className="px-6 py-5 border-r border-gray-100 w-32 text-right">कामाची रक्कम</th>
                                <th className="px-6 py-5 w-48 text-center">टोटल इंटरेस्टेड</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 text-[14px]">
                            {displayData.map((item, index) => (
                                <tr key={item.id} className="hover:bg-blue-50/40 transition-colors group">
                                    <td className="px-6 py-5 border-r border-gray-50 text-center font-bold text-gray-400">{index + 1}</td>
                                    <td className="px-6 py-5 border-r border-gray-50 font-bold text-gray-700">{item.workNumber}</td>
                                    <td className="px-6 py-5 border-r border-gray-50 font-bold text-gray-800 leading-relaxed">
                                        {item.workName}
                                    </td>
                                    <td className="px-6 py-5 border-r border-gray-50 font-bold text-gray-800 text-right">{item.tenderAmount}</td>
                                    <td className="px-6 py-5 text-center">
                                        <div className="flex items-center justify-center gap-2">
                                            <span className="font-bold text-gray-800">{item.interestedCount}</span>
                                            <span className="text-gray-300">-</span>
                                            <button 
                                                onClick={() => navigate(`/zp-head/reports/interested-detail/${item.id}`)}
                                                className="text-[#4e73df] hover:text-blue-800 font-bold text-sm underline decoration-blue-100 underline-offset-4"
                                            >
                                                इंटरेस्टेड अर्ज
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {displayData.length === 0 && (
                                <tr>
                                    <td colSpan="5" className="px-6 py-24 text-center">
                                        <div className="flex flex-col items-center gap-4 text-gray-300 font-bold">
                                            <FaSearch size={40} />
                                            <p className="italic">निकषांनुसार कोणतीही माहिती सापडली नाही.</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <style>{`
                .font-marathi {
                    font-family: 'Hind', sans-serif;
                }
                @import url('https://fonts.googleapis.com/css2?family=Hind:wght@400;500;600;700&display=swap');
            `}</style>
        </div>
    );
};

export default ZpHeadInterestedWorkByDate;
